import { Link, createFileRoute } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { SalesTable } from '@/components/sales/sales-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useMe } from '@/lib/clients';
import { useOrgId } from '@/lib/org';
import { useSalesTransactions } from '@/lib/sales';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sales/quotes/')({ component: Quotes });

const CHIPS: Array<[string, string]> = [
  ['all', 'All'],
  ['pending', 'Pending'],
  ['accepted', 'Accepted'],
  ['declined', 'Declined'],
  ['converted', 'Converted'],
];

/** Quotes list (document 06 S5; QBO Estimates). */
function Quotes() {
  const orgId = useOrgId();
  const me = useMe();
  const tx = useSalesTransactions();
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const rows = (tx.data ?? []).filter(
    (r) =>
      r.type === 'quote' &&
      (status === 'all' || r.status === status) &&
      (!needle ||
        r.contactName.toLowerCase().includes(needle) ||
        (r.number ?? '').toLowerCase().includes(needle)),
  );
  const newButton = me.data?.isStaff ? (
    <Button asChild>
      <Link to="/sales/quotes/new">New quote</Link>
    </Button>
  ) : null;
  return (
    <>
      <PageHeader title="Quotes" actions={newButton} />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : (
        <>
          <div
            className="mb-3 flex flex-wrap items-center gap-2"
            role="tablist"
            aria-label="Status"
          >
            {CHIPS.map(([k, label]) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={status === k}
                onClick={() => setStatus(k)}
                className={cn(
                  'rounded-full border px-3 py-1 text-sm',
                  status === k
                    ? 'border-primary bg-accent font-medium text-accent-foreground'
                    : 'bg-card text-muted-foreground hover:bg-muted',
                )}
              >
                {label}
              </button>
            ))}
            <div className="relative ml-auto w-[320px]">
              <Search className="pointer-events-none absolute left-2 top-2.5 size-4 text-muted-foreground" />
              <Input
                className="bg-card pl-8"
                placeholder="Search by customer or quote no."
                aria-label="Search quotes"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          </div>
          {tx.isPending ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <EmptyState title="No quotes match these filters" action={newButton ?? undefined} />
          ) : (
            <SalesTable rows={rows} showType={false} />
          )}
        </>
      )}
    </>
  );
}
